import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { TreeViewBox } from '~/components/elements/TreeViewBox'
import {
  BrandTreeView,
  type BrandTreeViewProps,
} from '~/components/features/BrandTreeView'
import { type Brand as BrandBase } from '~/gql/graphql'

type Brand = App.PickGraphQL<BrandBase, 'id' | 'name'>

export type BrandTreeViewBoxInnerProps<TBrand extends Brand> = {
  brands: TBrand[]
  'data-testid'?: string
  onSelectedBrandIdChange?: (brandIds: TBrand['id'][]) => void
  selectedBrandIds: TBrand['id'][]
}

export const BrandTreeViewBoxInner = <TBrand extends Brand>({
  brands,
  'data-testid': dataTestId,
  onSelectedBrandIdChange,
  selectedBrandIds,
}: BrandTreeViewBoxInnerProps<TBrand>) => {
  const { t } = useTranslation()
  const [filter, setFilter] = React.useState('')

  const unselectedBrands = React.useMemo(
    () =>
      brands.filter((brand) => {
        const hit = brand.name.toLowerCase().includes(filter.toLowerCase())
        const isSelected = selectedBrandIds.includes(brand.id)

        return hit && !isSelected
      }),
    [brands, filter, selectedBrandIds],
  )

  const selectedBrands = React.useMemo(
    () => brands.filter((brand) => selectedBrandIds.includes(brand.id)),
    [brands, selectedBrandIds],
  )

  const handleUnselectedTreeViewItemClick = React.useCallback<
    EH<BrandTreeViewProps<TBrand>, 'onItemClick'>
  >(
    (brandId) => {
      const newBrands = brands.filter(({ id }) =>
        [...selectedBrandIds, brandId].includes(id),
      )
      const newBrandIds = newBrands.map(R.prop('id'))
      onSelectedBrandIdChange?.(newBrandIds)
    },
    [onSelectedBrandIdChange, brands, selectedBrandIds],
  )

  const handleSelectedTreeViewItemClick = React.useCallback<
    EH<BrandTreeViewProps<TBrand>, 'onItemClick'>
  >(
    (brandId) => {
      const newBrandIds = R.difference(selectedBrandIds, [brandId])
      onSelectedBrandIdChange?.(newBrandIds)
    },
    [onSelectedBrandIdChange, selectedBrandIds],
  )

  return (
    <TreeViewBox
      data-testid={dataTestId}
      search={{
        filter,
        onFilterChange: setFilter,
      }}
      selectedTitle={t('selected_brands')}
      selectedTreeView={
        <BrandTreeView
          brands={selectedBrands}
          onItemClick={handleSelectedTreeViewItemClick}
        />
      }
      unselectedTitle={t('unselected_brands')}
      unselectedTreeView={
        <BrandTreeView
          brands={unselectedBrands}
          onItemClick={handleUnselectedTreeViewItemClick}
        />
      }
    />
  )
}
